import { useMemo, useState } from 'react';
import { UsersIcon } from '@heroicons/react/24/outline';
import { DataTable, SolidStatusPill, type DataTableColumn, type EventDto } from '@odo/shared-ui';
import { useEventSignups } from '../../hooks/useEvents';
import type { EventSignupRow } from '../../services/events';
import { UserCell } from '../accounts/users/UserCell';

interface EventSignupsPanelProps {
    event: EventDto;
}

// Matches the backend signups route's own default `limit`.
const SIGNUPS_PAGE_SIZE = 50;

const SIGNUP_STATUS_LABEL: Record<string, string> = {
    signed_up: 'Signed up',
    cancelled: 'Cancelled'
};

// Solid pairs for SolidStatusPill's `toneClassName`, same convention as the
// events list's status column.
const SIGNUP_STATUS_TONE: Record<string, string> = {
    signed_up: 'text-white bg-emerald-600',
    cancelled: 'text-white bg-gray-400'
};

// Same padding convention as practiceColumns.tsx: px-3 cells, first/last
// aligned with the page gutter.
const TD_PADDING = 'px-3 first:pl-5 last:pr-4 overflow-hidden';
const TH_PADDING = '!px-3 first:!pl-5 last:!pr-4';

const SKELETON_ROW_COUNT = 5;

function SkeletonRows() {
    const widths = ['w-52', 'w-24', 'w-32'];
    return (
        <>
            {Array.from({ length: SKELETON_ROW_COUNT }).map((_, i) => (
                <tr key={i} className="h-16">
                    {widths.map((width, j) => (
                        <td key={j} className="px-3 first:pl-5 align-middle">
                            <div className={`h-3.5 rounded bg-gray-200 animate-pulse ${width}`} />
                        </td>
                    ))}
                </tr>
            ))}
        </>
    );
}

function EmptyState({ isError }: { isError: boolean }) {
    return (
        <div className="flex flex-col items-center gap-3 text-center">
            <UsersIcon className="h-12 w-12 text-gray-300" strokeWidth={1} />
            <p className="text-sm text-gray-500">
                {isError
                    ? "Couldn't load the sign-ups for this event."
                    : 'Nobody has signed up yet.'}
            </p>
        </div>
    );
}

/** The attendee list for one event (cancellations included, not filtered out).
 *  The caller keys this by `event.id`, so `page` resets on remount for free. */
export function EventSignupsPanel({ event }: EventSignupsPanelProps) {
    const [page, setPage] = useState(1);

    const { signups, meta, isLoading, isError } = useEventSignups(event.id, {
        page,
        limit: SIGNUPS_PAGE_SIZE
    });

    const columns = useMemo<DataTableColumn<EventSignupRow>[]>(
        () => [
            {
                key: 'user',
                label: 'Practitioner',
                width: 'auto',
                cellClassName: TD_PADDING,
                headerClassName: TH_PADDING,
                render: (signup) => (
                    // `fullname` is nullable on users.users - fall back to the
                    // email so the avatar still has initials to derive.
                    <UserCell name={signup.fullname ?? signup.email} email={signup.email} />
                )
            },
            {
                key: 'status',
                label: 'Status',
                width: '18%',
                cellClassName: TD_PADDING,
                headerClassName: `${TH_PADDING} whitespace-nowrap`,
                render: (signup) => (
                    <SolidStatusPill
                        value={signup.status}
                        label={SIGNUP_STATUS_LABEL[signup.status] ?? signup.status}
                        toneClassName={SIGNUP_STATUS_TONE[signup.status]}
                    />
                )
            },
            {
                key: 'signedUpAt',
                label: 'Signed up',
                width: '26%',
                cellClassName: `${TD_PADDING} text-sm text-gray-500 whitespace-nowrap`,
                headerClassName: `${TH_PADDING} whitespace-nowrap`,
                render: (signup) =>
                    new Date(signup.signedUpAt).toLocaleString(undefined, {
                        month: 'short',
                        day: 'numeric',
                        year: 'numeric',
                        hour: 'numeric',
                        minute: '2-digit'
                    })
            }
        ],
        []
    );

    return (
        <div className="flex-1 min-h-0 flex flex-col overflow-hidden">
            <DataTable
                label="Sign-ups"
                columns={columns}
                rows={signups}
                getRowKey={(signup) => signup.userId}
                tableLayout="auto"
                rowHeightClassName="h-16"
                mode="paged"
                pagination={{
                    page: meta.page,
                    totalPages: meta.totalPages,
                    total: meta.total,
                    onPage: (p) => setPage(Math.min(Math.max(1, p), meta.totalPages))
                }}
                isLoading={isLoading}
                loadingRows={<SkeletonRows />}
                emptyState={<EmptyState isError={isError} />}
                emptyStateCellClassName="px-3 py-16"
                className="overflow-hidden px-8 pb-8"
            />
        </div>
    );
}
